import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@anchor/react-components/dist/lib/components";
import { McTooltip, McIcon, McModal, McButton } from "@maersk-global/mds-react-wrapper/components-core";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import UserInfo from "../../../context/UserAccessContext";
import { IsLocked } from "../../IsLocked/IsLocked";

const hasRealText = (html) => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold", "italic", "underline",
  "list", "bullet",
  "link",
];

function PrecedingEventsSection(props) {
  const [value, setValue] = useState(props.preceEventsDescription || "");
  const [modalOpen, setModalOpen] = useState(false);
  const savingRef = useRef(false);
  const { graphData } = UserInfo();

  useEffect(() => {
    setValue(props.preceEventsDescription || "");
  }, [props.preceEventsDescription]);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const onSave = () => {
    savingRef.current = true;
    props.dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "incidentSummaryState",
      payload: { ...props.incidentSummaryState, preceEventsDescription: value },
    });
    props.dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "changedFileds",
      payload: (prev) => ({ ...prev, preceEventsDescription: true }),
    });
    handleClose();
    if (props.onAutoSave) {
      setTimeout(() => { props.onAutoSave(); savingRef.current = false; }, 100);
    } else {
      savingRef.current = false;
    }
  };

  const isLocked = Array.isArray(props.lockedFields) &&
    props.lockedFields.find(f => f.fieldName === "preceEventsDescription" && f.lockedBy !== graphData?.username);

  return (
    <>
      <div id="section-preceding-events" className="sre-section-card">
        <div className="sre-section-header">
          <span className="sre-section-header-title">Preceding Events</span>
          {hasRealText(value) ? (
            <span className="sre-section-complete-badge">&#10003; Complete</span>
          ) : (
            <span className="sre-section-warning-badge">&#9888; Incomplete</span>
          )}
          <McTooltip position="bottom-right" fit="small" width={320}>
            <McIcon slot="trigger" icon="info-circle" size="16" style={{ cursor: "pointer", color: "#fff" }} />
            <div style={{ fontSize: "12px", lineHeight: "1.6", padding: "4px 0" }}>
              <strong>SRE Best Practices — Preceding Events</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: "16px" }}>
                <li>List any events, changes, or alerts that occurred before the incident</li>
                <li>Include deployments, configuration changes, or traffic spikes</li>
                <li>Note any early warning signs that were not acted upon</li>
                <li>Provide timestamps to establish a clear timeline</li>
              </ul>
            </div>
          </McTooltip>
          <span className="sre-section-header-badge" style={{ flex: 1 }}>
            Manual entry required
          </span>
          {!props.postmortemIsIncompleteState && (
            <button className="sre-section-header-action" onClick={handleOpen}>
              <Icon name="pencil" style={{ fontSize: "12px" }} />
              Edit
            </button>
          )}
        </div>

        <div
          className={`sre-section-body${!props.postmortemIsIncompleteState ? " sre-section-body--editable" : ""}`}
          onClick={!props.postmortemIsIncompleteState ? handleOpen : undefined}
        >
          <IsLocked field="preceEventsDescription" lockedFields={props.lockedFields} graphData={graphData} />
          <div style={{
            fontSize: "14px", lineHeight: 1.7,
            color: "var(--mds_brand_appearance_default_default_text-color, #141414)",
            wordBreak: "break-word",
            ...(isLocked ? { border: "1px solid red", padding: "10px", borderRadius: "5px" } : {}),
          }}>
            {hasRealText(value) ? (
              <div dangerouslySetInnerHTML={{ __html: value.replace(/\n/g, "<br/>") }} />
            ) : (
              <span style={{ color: "var(--mds_brand_appearance_default_default_text-color-secondary, #999)", fontStyle: "italic" }}>
                Describe any events that preceded or contributed to the incident
              </span>
            )}
          </div>
        </div>
      </div>

      <McModal
        heading="Preceding Events"
        open={modalOpen}
        dimension="medium"
        backdropcloseactiondisabled={true}
        escapecloseactiondisabled={true}
        closed={handleClose}
      >
        <div style={{ minHeight: "300px" }}>
          <ReactQuill
            theme="snow"
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            value={value}
            onChange={setValue}
            style={{ background: "#fff", minHeight: "240px" }}
          />
        </div>
        <McButton slot="primaryAction" appearance="primary" click={onSave}>
          Save
        </McButton>
        <McButton slot="secondaryAction" appearance="neutral" variant="outlined" click={handleClose}>
          Cancel
        </McButton>
      </McModal>
    </>
  );
}

export default PrecedingEventsSection;
